import { Pane, TextInputField, Alert } from "evergreen-ui";
import { useCallback, useEffect, useMemo, useState } from "react";
import {
    ContentPosition,
    DetailsPageSection,
} from "../../common/DetailsPageSection";
import { FooterActionBar } from "../../common/FooterActionBar";
import { FormConfig, useGenericForm, SubmitOperationConfig } from "../../common/hooks/useGenericForm";
import { useUpdateProductListingMutation } from "../../datastore/ProductListingDataStore";
import { StorePicker } from "../Store/StorePicker";
import { ProductListingUpdateInput } from "../../../../common/inputs/ProductListingInput";

interface ProductDetailsFormData {
    mainSku: string;
    storeId: string;
}

interface ProductDetailsTabProps {
    productListingId: string;
    mainSku: string | null | undefined;
    storeId: string | undefined;
    storeName: string | undefined;
}

export const ProductDetailsTab = ({
    productListingId,
    mainSku: initialMainSku,
    storeId: initialStoreId,
    storeName: initialStoreName,
}: ProductDetailsTabProps): JSX.Element => {
    const [
        updateProductListing,
        {
            loading: submitFormLoading,
            error: submitFormError,
            called: submitFormCalled,
            data: updatedProductListing,
        },
    ] = useUpdateProductListingMutation();

    // Trust the mutation response once it arrives; fall back to props only on
    // first render. Nullish coalescing against the props would drop legitimate
    // nulls from the response (e.g. after the user clears mainSku).
    const committedMainSku: string | null = updatedProductListing
        ? updatedProductListing.mainSku ?? null
        : initialMainSku ?? null;
    const committedStoreId: string = updatedProductListing
        ? updatedProductListing.store?.id ?? ""
        : initialStoreId ?? "";
    const committedStoreName: string = updatedProductListing
        ? updatedProductListing.store?.name ?? ""
        : initialStoreName ?? "";

    const submitOperationConfig: SubmitOperationConfig = useMemo(() => ({
        loading: submitFormLoading,
        called: submitFormCalled,
        error: submitFormError,
        successMessage: "Product listing updated successfully.",
        failureMessage: "Error updating product listing.",
    }), [submitFormLoading, submitFormCalled, submitFormError]);

    const formConfig: FormConfig<ProductDetailsFormData> = useMemo(() => ({
        submitOperationConfig,
        fields: [
            { name: "mainSku" as const, required: false },
            { name: "storeId" as const, required: false },
        ],
    }), [submitOperationConfig]);

    const initialValues: ProductDetailsFormData = useMemo(() => ({
        mainSku: committedMainSku ?? "",
        storeId: committedStoreId,
    }), [committedMainSku, committedStoreId]);

    const {
        formValues,
        setField,
        isFormDirty,
        isFormValid,
        formStatusMessage,
        resetForm,
    } = useGenericForm<ProductDetailsFormData>({ initialValues, formConfig });

    // Display name of the currently committed store. Lives outside the form so
    // that the picker's filter substring cannot dirty the form — only a
    // confirmed selection (via onSelect) mutates this value.
    const [displayedStoreName, setDisplayedStoreName] = useState<string>(committedStoreName);

    useEffect(() => {
        setDisplayedStoreName(committedStoreName);
    }, [committedStoreName]);

    const handleMainSkuChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
        setField("mainSku", e.target.value);
    }, [setField]);

    const handleStoreSelect = useCallback((storeId: string, storeName: string) => {
        if (storeId === formValues.storeId) {
            return;
        }
        setField("storeId", storeId);
        setDisplayedStoreName(storeName);
    }, [setField, formValues.storeId]);

    const handleSave = useCallback(() => {
        const input: ProductListingUpdateInput = {
            id: productListingId,
            mainSku: formValues.mainSku.trim() || null,
        };
        if (formValues.storeId) {
            input.storeId = formValues.storeId;
        }
        updateProductListing(input);
    }, [productListingId, formValues.mainSku, formValues.storeId, updateProductListing]);

    const handleDiscard = useCallback(() => {
        resetForm();
        setDisplayedStoreName(committedStoreName);
    }, [resetForm, committedStoreName]);

    const mutationErrorMessage = !submitFormLoading && submitFormCalled && submitFormError
        ? (submitFormError.message || "Error updating product listing.")
        : "";

    return (
        <Pane>
            <DetailsPageSection
                contentPosition={ContentPosition.left}
                hideBorder={true}
            >
                <Pane className="jc-create-store-custom-colors" marginTop={24}>
                    <TextInputField
                        name="mainSku"
                        label="Main SKU"
                        maxWidth="620px"
                        value={formValues.mainSku}
                        className="jc-create-store-input"
                        onChange={handleMainSkuChange}
                        placeholder="Enter main SKU"
                    />
                    <StorePicker
                        label="Assigned Store"
                        value={displayedStoreName}
                        onSelect={handleStoreSelect}
                        placeholder="Search for a store"
                        className="jc-create-store-input"
                        width="620px"
                    />
                    {mutationErrorMessage && (
                        <Alert
                            intent="danger"
                            title={mutationErrorMessage}
                            marginTop={8}
                            maxWidth="620px"
                        />
                    )}
                </Pane>
            </DetailsPageSection>

            {isFormDirty && (
                <FooterActionBar
                    primaryButtonText="Save"
                    isPrimaryLoading={submitFormCalled && submitFormLoading}
                    primaryButtonDisabled={
                        !isFormValid || (submitFormCalled && submitFormLoading)
                    }
                    onPrimaryClick={handleSave}
                    showSecondaryButton={false}
                    tertiaryButtonText="Discard Changes"
                    onTertiaryClick={handleDiscard}
                    infoComponent={<p>{formStatusMessage}</p>}
                />
            )}
        </Pane>
    );
};
